import React, { useState, useEffect, createRef } from "react";
// 3rd party
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import { keyframes } from "@mui/system";

import {
  ref,
  getDownloadURL,
  uploadBytesResumable,
  listAll,
  updateMetadata,
  getMetadata,
} from "firebase/storage";
import Dropzone from "react-dropzone";

// mui
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/styles";
import { useRouter } from "next/router";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
// lib
import deleteFromUploadLog from "lib/deleteFromUploadLog";
import firebaseDownloadFile from "components/Loans/LoanStepper/components/lib/firebaseDownloadFile";
import firebaseDeleteFile from "components/Loans/LoanStepper/components/lib/firebaseDeleteFile";
import pushToUploadLog from "lib/pustToUploadLog";
import calculateAndSetPercentCompleted from "components/Loans/LoanContainer/utils/calculateAndSetPercentCompleted";
import docLogStatusColor from "components/Loans/LoanContainer/utils/docLogStatusColor";
import renameFBFile from "components/Loans/LoanContainer/utils/renameFBFile";
import { borderRadius } from "components/listings/DnDComponents/constants";

import updateLoanUploadLog from "components/listings/Uploader/utils/updateLoanUploadLog";
import LoadingModal from "components/Firebase/FirebaseUploader/components/LoadingModal";
import DocLogActions from "components/Loans/LoanContainer/components/DocLogActions";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default function FirebaseUploader({
  customUploadResFunction,
  setUploadingInProgress,
  customText,
  highlight,
  loan,
  stepID,
  acceptFileTypes,
  collectionType = "loans",
  field,
  loanSectionController,
  setLoanSectionController,
  hideDropZone,
  setExternalFiles,
  repullFiles,
  setRepullFiles,
}) {
  const { user, firebaseStorage, userRoles, userPermissions, setUserLoans } =
    useStore(AppStore);

  const theme = useTheme();
  const router = useRouter();

  const [focusedID, setFocusedID] = useState(null);
  const [logPreviewURL, setLogPreviewURL] = useState(false);
  const [showLogPreview, setShowLogPreview] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const [showRenameFile, setShowRenameFile] = useState({});
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [files, setFiles] = useState([]);
  const [localUploadingInProgress, setLocalUploadingInProgress] =
    useState(false);

  const handleClose = () => setOpen(false);
  function pullFiles() {
    const path = `client_files/${loan.UID}/loans/${loan.LoanIDCypher}/${stepID}/`;

    setPath(path);

    (async () => {
      try {
        const storageRef = ref(firebaseStorage, path);

        const res = await listAll(storageRef);

        const metaDatas = [];
        for (const item of res.items) {
          const metaData = await getMetadata(item);
          metaDatas.push(metaData);
        }

        const filteredItems = metaDatas
          .filter((item) => {
            return (
              item?.customMetadata?.sectionID === stepID &&
              item?.customMetadata?.sectionField === field
            );
          })
          .map((item) => {
            // NOTE, when will name edits, they need to be updated in the uploadLog as well
            const uploadLogFD = loan.UploadLog.filter((logItem) => {
              return logItem.name === item.name;
            });

            // combine FB and uploadLog data
            const uItem = {};
            uItem.name = item.name;
            uItem.timeCreated = item.timeCreated;
            uItem.updated = item.updated;
            uItem.status =
              uploadLogFD?.[0]?.status &&
              capitalizeFirstLetter(uploadLogFD?.[0]?.status);
            uItem.feedback = uploadLogFD?.[0]?.feedback;
            uItem.downloadURL = item?.customMetadata?.downloadURL;

            return uItem;
          });

        // sort by latest updated
        setFiles(
          filteredItems.sort((a, b) => {
            const dateA = new Date(a.updated);
            const dateB = new Date(b.updated);
            return dateB - dateA;
          })
        );
      } catch (error) {
        console.log(error);
      }
    })();
  }

  useEffect(() => {
    // make path

    if (loan && path === "") {
      // set uploading animation there

      pullFiles();
    }
  }, [loan]);

  useEffect(() => {
    if (repullFiles) {
      pullFiles();

      setRepullFiles(false);
    }
  }, [repullFiles]);

  useEffect(() => {
    if (files.length > 0) {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    }
    setExternalFiles && setExternalFiles(files);
  }, [files]);

  const dropzoneRef = createRef();
  const maxFilesToAccept = 0; // 0 maxFiles means unlimited, might expose later

  const onDrop = (acceptedFiles) => {
    // do I really need ${generateRandomString(6)
    // to append to file name? I guess if I want to version
    // and prevent overwrites, I can bring it back ${generateRandomString(6)
    if (!acceptedFiles) return null;

    const uploadTasks = [];
    let totalBytes = 0;
    let uploadedBytes = 0;

    for (const file of acceptedFiles) {
      const filePath = path + `${file.name}`;

      const storageRef = ref(firebaseStorage, filePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTasks.push(uploadTask);
      totalBytes += file.size;

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          if (!open) {
            setOpen(true);
          }

          uploadedBytes += snapshot.bytesTransferred;

          const combinedProgress = Math.min(
            Math.floor((uploadedBytes / totalBytes) * 100),
            100
          );

          setUploadProgress(combinedProgress);
        },
        (error) => {
          console.error(error);
        },
        () => {
          // console.log("File uploaded successfully!");
        }
      );
    }

    (async () => {
      const res = await Promise.all(uploadTasks);

      for (const re of res) {
        const storageRef = re.ref;

        const newMetadata = {
          customMetadata: {
            sectionID: stepID,
            sectionField: field,
          },
        };

        const updatedMetadata = await updateMetadata(storageRef, newMetadata);
      }

      const filesToSet = [
        ...res.map((file) => {
          return file.metadata;
        }),
        ...files,
      ];

      const uploadLogEntries = res
        .map((file) => {
          return file.metadata;
        })
        .map((file) => {
          // upload log item
          return {
            id: [...Array(30)]
              .map(() => Math.random().toString(36)[2])
              .join(""),
            created_at: file.timeCreated,
            name: file.name,
            sectionID: stepID,
            field: field,
            status: "unread",
          };
        });
      // send this to update the upload log
      (async () => {
        await pushToUploadLog(user, loan.id, uploadLogEntries);

        setFiles(filesToSet);
        customUploadResFunction(filesToSet);

        setOpen(false);
      })();
    })();
  };

  return (
    <Box
      sx={{
        "&:hover": {
          cursor: "pointer",
        },
      }}
    >
      {!hideDropZone && (
        <Dropzone
          ref={dropzoneRef}
          accept={
            acceptFileTypes
              ? acceptFileTypes
              : collectionType === "home-listing"
              ? "image/*"
              : ".pdf,.xlsx,.xls"
          }
          maxFiles={maxFilesToAccept ? maxFilesToAccept : 0} // 0 maxFiles means unlimited
          onDrop={onDrop}
        >
          {({ getRootProps, getInputProps, isFocused }) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                fontFamily: "sans-serif",
                overflow: "hidden",
              }}
            >
              <Box // InnerDiv
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "20px",
                  borderWidth: "2px",
                  borderRadius: "5px",
                  borderColor: highlight ? "red" : "#eeeeee",
                  borderStyle: "dashed",
                  backgroundColor: "#fafafa",
                  color: highlight ? "red" : "#bdbdbd",
                  outline: "none",
                  transition: "border 0.24s ease-in-out",
                  "& :focus": {
                    borderColor: "#2196f3",
                  },
                }}
                highlight={true} // always highlight now cause ppl think its disabled
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <Box sx={{ fontSize: "1rem" }}>
                  {customText
                    ? customText
                    : "Drag 'n' drop pictures here, or click to select from computer"}
                </Box>
              </Box>
            </Box>
          )}
        </Dropzone>
      )}
      <LoadingModal
        open={open}
        handleClose={handleClose}
        uploadProgress={uploadProgress}
      />

      {files?.length > 0 &&
        files.map((file, index) => {
          const docLogEntry = loan?.UploadLog?.filter((item) => {
            return item.name === file.name;
          })[0];

          return (
            <Box
              key={`file_list_${index}_item_for_${stepID}_${field}_${file.timeCreated}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: theme.spacing(1),
                fontSize: theme.spacing(2),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "90%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "block",
                      textAlign: "left",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "75%",
                      overflow: "hidden",
                      width: "75%",
                      margin: theme.spacing("auto", 0),
                    }}
                  >
                    {docLogEntry?.id && showRenameFile[docLogEntry?.id] ? (
                      <TextField
                        label="Rename"
                        defaultValue={
                          docLogEntry.name ? docLogEntry.name : null
                        }
                        onBlur={(e) => {
                          const uploadLog = loan.UploadLog;
                          const [logEntry] = uploadLog.filter((logEntry) => {
                            return logEntry.id === docLogEntry?.id;
                          });
                          const filePath = `client_files/${loan.UID}/loans/${loan.LoanIDCypher}/${logEntry.sectionID}/${logEntry.name}`;
                          logEntry.name = e.target.value;
                          (async () => {
                            let updatedLoan = await updateLoanUploadLog(
                              user,
                              logEntry, // old:uploadLog,
                              loan,
                              false
                            );
                            updatedLoan = Object.assign({}, updatedLoan);
                            setUserLoans([updatedLoan]);

                            const updatedShowRenameFile = Object.assign(
                              {},
                              showRenameFile
                            );
                            updatedShowRenameFile[logEntry.id] = false;
                            setShowRenameFile(updatedShowRenameFile);
                            const newName = e.target.value;
                            await renameFBFile(user, loan, filePath, newName);
                            pullFiles();
                          })();
                        }}
                      />
                    ) : docLogEntry?.name ? (
                      docLogEntry?.name
                    ) : (
                      file?.name
                    )}
                  </Box>
                  <Box
                    sx={{
                      textAlign: "center",
                      width: "25%",
                      margin: theme.spacing("auto", 0),
                      color: docLogEntry?.status
                        ? docLogStatusColor(
                            docLogEntry?.status?.toLowerCase(),
                            theme
                          )
                        : "inherit",
                    }}
                  >
                    {docLogEntry?.status?.includes("approved_")
                      ? "Approved"
                      : docLogEntry?.status?.includes("rejected_")
                      ? "Rejected"
                      : docLogEntry?.status
                      ? docLogEntry?.status?.charAt(0).toUpperCase() +
                        docLogEntry?.status?.slice(1)
                      : null}
                  </Box>
                  {userRoles.length > 0 && userPermissions.Role && (
                    <Box // {/* {entry.status === "rejected" && ( */}
                      sx={{
                        width: focusedID === docLogEntry?.id ? "60%" : "10%",
                        color: docLogEntry?.status
                          ? docLogStatusColor(docLogEntry?.status, theme)
                          : "inherit",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        sx={{ width: "100%" }}
                        label="Feedback"
                        defaultValue={
                          docLogEntry?.feedback ? docLogEntry.feedback : null
                        }
                        onFocus={() => setFocusedID(docLogEntry?.id)}
                        onBlur={(e) => {
                          const uploadLog = loan.UploadLog;
                          const [logEntry] = uploadLog.filter((logEntry) => {
                            return logEntry.id === docLogEntry?.id;
                          });
                          logEntry.feedback = e.target.value;

                          setFocusedID(null);

                          (async () => {
                            let updatedLoan = await updateLoanUploadLog(
                              user,
                              logEntry,
                              loan,
                              false
                            );
                            updatedLoan = Object.assign({}, updatedLoan);
                            setUserLoans([updatedLoan]);
                          })();
                        }}
                      />
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    margin: theme.spacing("auto"),
                  }}
                >
                  {docLogEntry &&
                  userRoles.length > 0 &&
                  userPermissions.Role ? (
                    <DocLogActions
                      key={`doc_log_action_${index}_item_for_${stepID}_${field}_${file.timeCreated}`}
                      setAnchorEl={setAnchorEl}
                      loan={loan}
                      anchorEl={anchorEl}
                      entry={docLogEntry}
                      showLogPreview={showLogPreview}
                      setShowLogPreview={setShowLogPreview}
                      logPreviewURL={logPreviewURL}
                      setLogPreviewURL={setLogPreviewURL}
                      setUserLoans={setUserLoans}
                      showRenameFile={showRenameFile}
                      setShowRenameFile={setShowRenameFile}
                    />
                  ) : (
                    <>
                      <IconButton
                        sx={{ display: "flex", alignItems: "center" }}
                        color="primary"
                        onClick={(event) => {
                          setAnchorEl({
                            ...anchorEl,
                            [docLogEntry?.id]: event.currentTarget,
                          });
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl[docLogEntry?.id]}
                        open={Boolean(anchorEl[docLogEntry?.id])}
                        onClose={() => {
                          setAnchorEl({
                            ...anchorEl,
                            [docLogEntry?.id]: null,
                          });
                        }}
                      >
                        <MenuItem
                          onClick={async () => {
                            (async () => {
                              const storageRef = ref(
                                firebaseStorage,
                                path + `${file.name}`
                              );

                              await firebaseDownloadFile(storageRef);
                            })();
                          }}
                        >
                          Download
                        </MenuItem>
                        {!hideDropZone && ( // if dropzone is hidden also don't allow delete
                          <MenuItem
                            onClick={() => {
                              const storageRef = ref(
                                firebaseStorage,
                                path + `${file.name}`
                              );

                              (async () => {
                                await firebaseDeleteFile(
                                  storageRef,
                                  files,
                                  setFiles
                                );

                                // also need to clean up doc log
                                await deleteFromUploadLog(
                                  user,
                                  loan.id,
                                  file.timeCreated
                                );

                                // NOTE: timeCreated is the unique identifier for the file
                                const filesToSet = files.filter((f) => {
                                  return f.timeCreated !== file.timeCreated;
                                });

                                setFiles(filesToSet);
                                customUploadResFunction(filesToSet);
                              })();
                            }}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
              <Box>
                <Collapse in={file.feedback}>
                  <Alert sx={{ margin: theme.spacing(1, 0) }} severity="error">
                    {file.feedback}
                  </Alert>
                </Collapse>
              </Box>
            </Box>
          );
        })}
    </Box>
  );
}
